import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import useBodyScrollLock from "../hooks/useBodyScrollLock";
import {
  approveBudgetSubmission,
  createBudgetRemark,
  getBudgetAudit,
  getBudgetSubmission,
  rejectBudgetSubmission,
  reopenBudgetSubmission,
  submitBudgetForApproval,
  updateBudgetSubmission,
  updateCommonSpend,
} from "../api/budgetApi";
import { formatInr } from "../utils/formatInr";
import { BUDGET_PLAN_SCOPE, BUDGET_PLAN_SCOPE_LABELS } from "../constants/budgetPlanScope";

const STATUS_STYLES = {
  draft: { bg: "#f1f5f9", color: "#475569", label: "Draft" },
  pending: { bg: "#fef3c7", color: "#b45309", label: "Pending" },
  approved: { bg: "#dcfce7", color: "#15803d", label: "Approved" },
  rejected: { bg: "#fee2e2", color: "#b91c1c", label: "Rejected" },
};

const btn = (bg, color = "#fff") => ({
  border: "none",
  borderRadius: 9,
  padding: "8px 14px",
  background: bg,
  color,
  fontWeight: 700,
  fontSize: "0.82rem",
  cursor: "pointer",
});

const fieldStyle = {
  width: "100%",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  padding: "8px 10px",
  fontSize: "0.88rem",
};

export default function BudgetApprovalDetailModal({ submissionId, onClose, isSuper, onSelectPlan, onBudgetChange }) {
  const qc = useQueryClient();
  // Mounted only while a submission is selected, so this is always "open".
  useBodyScrollLock(true);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editPlannedAmount, setEditPlannedAmount] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [commonAmount, setCommonAmount] = useState("");
  const [commonNotes, setCommonNotes] = useState("");
  const [newRemark, setNewRemark] = useState("");
  const [showAudit, setShowAudit] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["budget-submission-detail", submissionId],
    queryFn: () => getBudgetSubmission(submissionId).then((r) => r.data),
    enabled: Boolean(submissionId),
  });

  const submission = data?.submission;
  const canEditPlan =
    !isSuper && submission && (submission.status === "draft" || submission.status === "rejected");

  useEffect(() => {
    if (!submission) return;
    setEditTitle(submission.title || "");
    setEditPlannedAmount(
      submission.plannedAmountInr != null ? String(submission.plannedAmountInr) : ""
    );
    setEditNotes(submission.notes || "");
    onSelectPlan?.(submission.id);
  }, [submission?.id, submission?.title, submission?.plannedAmountInr, submission?.notes, onSelectPlan]);

  useEffect(() => {
    const cs = data?.commonSpend;
    if (!cs) return;
    setCommonAmount(cs.amountInr != null ? String(cs.amountInr) : "0");
    setCommonNotes(cs.notes || "");
  }, [data?.commonSpend?.id, data?.commonSpend?.amountInr, data?.commonSpend?.notes]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["budget-submission-detail"] });
    qc.invalidateQueries({ queryKey: ["budget-submissions"] });
    qc.invalidateQueries({ queryKey: ["budget-submissions-pending"] });
    qc.invalidateQueries({ queryKey: ["budget-submission-current"] });
    qc.invalidateQueries({ queryKey: ["budget-portfolio"] });
    qc.invalidateQueries({ queryKey: ["budget-summary"] });
    onBudgetChange?.();
  };

  const commonMut = useMutation({
    mutationFn: (body) => updateCommonSpend(submissionId, body).then((r) => r.data),
    onSuccess: () => {
      toast.success("Common department spend saved");
      invalidate();
      refetch();
    },
    onError: (e) => toast.error(e.response?.data?.message || "Save failed"),
  });

  const remarkMut = useMutation({
    mutationFn: (remark) => createBudgetRemark(submissionId, { remark }).then((r) => r.data),
    onSuccess: () => {
      toast.success("Remark added");
      setNewRemark("");
      invalidate();
      refetch();
    },
    onError: (e) => toast.error(e.response?.data?.message || "Failed to add remark"),
  });

  const auditQuery = useQuery({
    queryKey: ["budget-audit", submissionId],
    queryFn: () => getBudgetAudit(submissionId).then((r) => r.data),
    enabled: Boolean(submissionId) && showAudit,
  });

  const saveMut = useMutation({
    mutationFn: (body) => updateBudgetSubmission(submissionId, body).then((r) => r.data),
    onSuccess: () => {
      toast.success("Plan saved");
      invalidate();
      refetch();
    },
    onError: (e) => toast.error(e.response?.data?.message || "Save failed"),
  });

  const submitMut = useMutation({
    mutationFn: (notes) => submitBudgetForApproval(submissionId, { notes }),
    onSuccess: () => {
      toast.success("Submitted for superadmin approval");
      invalidate();
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.message || "Submit failed"),
  });

  const approveMut = useMutation({
    mutationFn: () => approveBudgetSubmission(submissionId).then((r) => r.data),
    onSuccess: (data) => {
      const applied = data?.appliedTotalAllocated;
      toast.success(
        applied != null
          ? `Budget approved — ${formatInr(applied)} allocated across projects`
          : "Budget approved — allocations updated"
      );
      invalidate();
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.message || "Approve failed"),
  });

  const rejectMut = useMutation({
    mutationFn: () => rejectBudgetSubmission(submissionId, { rejectionReason: rejectReason.trim() }),
    onSuccess: () => {
      toast.success("Budget rejected");
      invalidate();
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.message || "Reject failed"),
  });

  const reopenMut = useMutation({
    mutationFn: () => reopenBudgetSubmission(submissionId),
    onSuccess: () => {
      toast.success("Budget reopened for edits");
      invalidate();
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.message || "Reopen failed"),
  });

  const projects = data?.projects || [];
  const summary = data?.summary || {};
  const remarks = data?.remarks || [];
  const validation = data?.validation || {};
  const statusStyle = STATUS_STYLES[submission?.status] || STATUS_STYLES.draft;
  const isDeptScope = submission?.scopeType === BUDGET_PLAN_SCOPE.DEPARTMENT;
  const showCommonSpendEdit = canEditPlan && isDeptScope;

  const handleSave = () => {
    if (!editTitle.trim()) {
      toast.error("Plan name is required");
      return;
    }
    if (!editPlannedAmount || Number(editPlannedAmount) < 0) {
      toast.error("Requested budget amount is required");
      return;
    }
    saveMut.mutate({
      title: editTitle.trim(),
      plannedAmountInr: Number(editPlannedAmount),
      notes: editNotes.trim() || null,
    });
  };

  const handleSubmit = () => {
    if (!editPlannedAmount || Number(editPlannedAmount) < 0) {
      toast.error("Set requested budget before submitting");
      return;
    }
    submitMut.mutate(editNotes.trim() || undefined);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1500,
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          width: "100%",
          maxWidth: 720,
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 50px rgba(15,23,42,0.2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "18px 20px", borderBottom: "1px solid #e2e8f0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: "1.1rem", color: "#0f172a" }}>
                {canEditPlan ? "Edit budget plan" : submission?.title || "Budget plan"}
              </div>
              {submission ? (
                <div style={{ fontSize: "0.82rem", color: "#64748b", marginTop: 4 }}>
                  {submission.department} · {submission.budgetYear}
                  {submission.organization ? ` · ${submission.organization}` : ""}
                  {" · "}
                  {submission.scopeType === "project"
                    ? `Project: ${submission.projectName || submission.projectId}`
                    : BUDGET_PLAN_SCOPE_LABELS.department}
                  {submission.submittedByName && isSuper ? ` · by ${submission.submittedByName}` : ""}
                </div>
              ) : null}
            </div>
            {submission ? (
              <span
                style={{
                  padding: "4px 10px",
                  borderRadius: 999,
                  fontSize: "0.72rem",
                  fontWeight: 800,
                  background: statusStyle.bg,
                  color: statusStyle.color,
                }}
              >
                {statusStyle.label}
              </span>
            ) : null}
          </div>
        </div>

        <div style={{ padding: "16px 20px", overflowY: "auto", flex: 1 }}>
          {isLoading ? (
            <div style={{ color: "#64748b" }}>Loading budget details…</div>
          ) : (
            <>
              {canEditPlan ? (
                <div
                  style={{
                    marginBottom: 16,
                    padding: "12px 14px",
                    borderRadius: 10,
                    background: "#EAF6FD",
                    border: "1px solid #a9ddf5",
                    fontSize: "0.82rem",
                    color: "#1272B3",
                  }}
                >
                  Update plan details here, then use <strong>Manage</strong> on projects in the table below to
                  edit budget lines. Save changes before final submission.
                </div>
              ) : null}

              {validation && (!validation.orgCapOk || !validation.deptCapOk) ? (
                <div
                  style={{
                    marginBottom: 16,
                    padding: "12px 14px",
                    borderRadius: 10,
                    background: "#fff7ed",
                    border: "1px solid #fdba74",
                    fontSize: "0.82rem",
                    color: "#9a3412",
                  }}
                >
                  <strong>Budget validation</strong>
                  {!validation.deptCapOk && validation.deptCapMessage ? (
                    <div style={{ marginTop: 6 }}>{validation.deptCapMessage}</div>
                  ) : null}
                  {!validation.orgCapOk && validation.orgCapMessage ? (
                    <div style={{ marginTop: 6 }}>{validation.orgCapMessage}</div>
                  ) : null}
                </div>
              ) : null}

              {canEditPlan ? (
                <div style={{ display: "grid", gap: 12, marginBottom: 16 }}>
                  <div>
                    <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#64748b", marginBottom: 4 }}>
                      Plan name *
                    </label>
                    <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} style={fieldStyle} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#64748b", marginBottom: 4 }}>
                      Requested budget (INR) *
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={editPlannedAmount}
                      onChange={(e) => setEditPlannedAmount(e.target.value)}
                      style={fieldStyle}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#64748b", marginBottom: 4 }}>
                      Notes for superadmin
                    </label>
                    <textarea
                      rows={2}
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      style={{ ...fieldStyle, resize: "vertical" }}
                      placeholder="Optional context for approval"
                    />
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                    gap: 10,
                    marginBottom: 16,
                  }}
                >
                  <div style={{ background: "#ecfdf5", borderRadius: 10, padding: "12px 14px", border: "1px solid #a7f3d0" }}>
                    <div style={{ fontSize: "0.7rem", color: "#047857", fontWeight: 700 }}>REQUESTED BUDGET</div>
                    <div style={{ fontWeight: 800, marginTop: 4, fontSize: "1.15rem", color: "#065f46" }}>
                      {submission?.plannedAmountInr != null ? formatInr(submission.plannedAmountInr) : "—"}
                    </div>
                  </div>
                  <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 12px" }}>
                    <div style={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 700 }}>PROJECTS IN SCOPE</div>
                    <div style={{ fontWeight: 800, marginTop: 4 }}>{summary.projectCount ?? 0}</div>
                  </div>
                  <div style={{ background: "#f0fdf4", borderRadius: 10, padding: "10px 12px" }}>
                    <div style={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 700 }}>ALLOCATED (SYSTEM)</div>
                    <div style={{ fontWeight: 800, marginTop: 4 }}>{formatInr(summary.totalAllocated)}</div>
                  </div>
                  <div style={{ background: "#EAF6FD", borderRadius: 10, padding: "10px 12px" }}>
                    <div style={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 700 }}>SPEND TO DATE</div>
                    <div style={{ fontWeight: 800, marginTop: 4 }}>{formatInr(summary.totalSpend)}</div>
                  </div>
                  {isDeptScope ? (
                    <div style={{ background: "#faf5ff", borderRadius: 10, padding: "10px 12px" }}>
                      <div style={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 700 }}>COMMON DEPT SPEND</div>
                      <div style={{ fontWeight: 800, marginTop: 4 }}>{formatInr(summary.commonDeptSpend)}</div>
                    </div>
                  ) : null}
                </div>
              )}

              {showCommonSpendEdit ? (
                <div
                  style={{
                    marginBottom: 16,
                    padding: "14px",
                    borderRadius: 10,
                    border: "1px solid #e9d5ff",
                    background: "#faf5ff",
                  }}
                >
                  <div style={{ fontWeight: 800, fontSize: "0.85rem", marginBottom: 10, color: "#6b21a8" }}>
                    Common department spend
                  </div>
                  <div style={{ display: "grid", gap: 10 }}>
                    <div>
                      <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#64748b", marginBottom: 4 }}>
                        Pool amount (INR) — shared / miscellaneous costs
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={commonAmount}
                        onChange={(e) => setCommonAmount(e.target.value)}
                        style={fieldStyle}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#64748b", marginBottom: 4 }}>
                        Notes
                      </label>
                      <input
                        value={commonNotes}
                        onChange={(e) => setCommonNotes(e.target.value)}
                        style={fieldStyle}
                        placeholder="e.g. team events, shared tools"
                      />
                    </div>
                    <div>
                      <button
                        type="button"
                        style={btn("#7c3aed")}
                        disabled={commonMut.isPending}
                        onClick={() =>
                          commonMut.mutate({
                            amountInr: Number(commonAmount) || 0,
                            notes: commonNotes.trim() || undefined,
                          })
                        }
                      >
                        Save common spend
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {submission?.rejectionReason ? (
                <div
                  style={{
                    marginBottom: 12,
                    padding: "10px 12px",
                    borderRadius: 8,
                    background: "#fef2f2",
                    color: "#b91c1c",
                    fontSize: "0.85rem",
                  }}
                >
                  <strong>Rejection reason:</strong> {submission.rejectionReason}
                </div>
              ) : null}

              {!canEditPlan && submission?.notes ? (
                <div style={{ marginBottom: 12, fontSize: "0.85rem", color: "#475569" }}>
                  <strong>Notes:</strong> {submission.notes}
                </div>
              ) : null}

              {(isSuper && (submission?.status === "pending" || submission?.status === "approved")) || remarks.length > 0 ? (
                <div style={{ marginBottom: 16, marginTop: 8 }}>
                  <div style={{ fontWeight: 800, fontSize: "0.85rem", marginBottom: 8 }}>Superadmin remarks</div>
                  {remarks.length === 0 ? (
                    <div style={{ fontSize: "0.82rem", color: "#64748b", marginBottom: 8 }}>No remarks yet.</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
                      {remarks.map((r) => (
                        <div
                          key={r.id}
                          style={{
                            padding: "10px 12px",
                            borderRadius: 8,
                            background: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            fontSize: "0.82rem",
                          }}
                        >
                          <div style={{ fontWeight: 700, color: "#334155" }}>{r.authorName || "Superadmin"}</div>
                          <div style={{ marginTop: 4, color: "#475569" }}>{r.remark}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {isSuper && (submission?.status === "pending" || submission?.status === "approved") ? (
                    <div style={{ display: "flex", gap: 8 }}>
                      <input
                        value={newRemark}
                        onChange={(e) => setNewRemark(e.target.value)}
                        placeholder="Add a remark on this breakdown…"
                        style={{ ...fieldStyle, flex: 1 }}
                      />
                      <button
                        type="button"
                        style={btn("#475569")}
                        disabled={!newRemark.trim() || remarkMut.isPending}
                        onClick={() => remarkMut.mutate(newRemark.trim())}
                      >
                        Add
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div style={{ marginBottom: 12 }}>
                <button
                  type="button"
                  style={{ ...btn("#f1f5f9", "#334155"), fontSize: "0.75rem" }}
                  onClick={() => setShowAudit((v) => !v)}
                >
                  {showAudit ? "Hide" : "Show"} audit log
                </button>
                {showAudit ? (
                  <div style={{ marginTop: 10, maxHeight: 160, overflowY: "auto", fontSize: "0.78rem" }}>
                    {auditQuery.isLoading ? (
                      <div style={{ color: "#64748b" }}>Loading audit…</div>
                    ) : (auditQuery.data || []).length === 0 ? (
                      <div style={{ color: "#64748b" }}>No audit entries.</div>
                    ) : (
                      (auditQuery.data || []).map((e) => (
                        <div key={e.id} style={{ padding: "6px 0", borderBottom: "1px solid #f1f5f9", color: "#475569" }}>
                          <strong>{e.action}</strong> · {e.actorName || "System"} ·{" "}
                          {e.createdAt ? new Date(e.createdAt).toLocaleString("en-IN") : ""}
                        </div>
                      ))
                    )}
                  </div>
                ) : null}
              </div>

              <div style={{ fontWeight: 800, fontSize: "0.85rem", marginBottom: 8 }}>
                {submission?.scopeType === "project" ? "Project in this plan" : "Projects in this plan"}
              </div>
              {projects.length === 0 ? (
                <div style={{ color: "#64748b", fontSize: "0.85rem" }}>No projects in scope.</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", textAlign: "left" }}>
                      <th style={{ padding: "8px 10px" }}>Project</th>
                      <th style={{ padding: "8px 10px" }}>Allocated</th>
                      <th style={{ padding: "8px 10px" }}>Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((p) => (
                      <tr key={p.projectId} style={{ borderTop: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "8px 10px", fontWeight: 600 }}>{p.projectName}</td>
                        <td style={{ padding: "8px 10px" }}>{formatInr(p.budgetAllocated)}</td>
                        <td style={{ padding: "8px 10px" }}>{formatInr(p.ytdSpend)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {showReject ? (
                <div style={{ marginTop: 16 }}>
                  <textarea
                    rows={3}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Reason for rejection (required)"
                    style={{ width: "100%", borderRadius: 8, border: "1px solid #cbd5e1", padding: 10 }}
                  />
                </div>
              ) : null}
            </>
          )}
        </div>

        <div
          style={{
            padding: "14px 20px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <button type="button" style={btn("#e2e8f0", "#334155")} onClick={onClose}>
            Close
          </button>

          {canEditPlan ? (
            <>
              <button type="button" style={btn("#0f766e")} onClick={handleSave} disabled={saveMut.isPending}>
                Save changes
              </button>
              <button type="button" style={btn("#2C63AE")} onClick={handleSubmit} disabled={submitMut.isPending}>
                Submit for approval
              </button>
            </>
          ) : null}

          {isSuper && submission?.status === "approved" ? (
            <button type="button" style={btn("#64748b")} onClick={() => reopenMut.mutate()} disabled={reopenMut.isPending}>
              Reopen
            </button>
          ) : null}

          {isSuper && submission?.status === "pending" ? (
            <>
              {!showReject ? (
                <button type="button" style={btn("#dc2626")} onClick={() => setShowReject(true)}>
                  Reject
                </button>
              ) : (
                <button
                  type="button"
                  style={btn("#dc2626")}
                  disabled={!rejectReason.trim() || rejectMut.isPending}
                  onClick={() => rejectMut.mutate()}
                >
                  Confirm reject
                </button>
              )}
              <button type="button" style={btn("#15803d")} onClick={() => approveMut.mutate()} disabled={approveMut.isPending}>
                Approve
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
